/**
 * Financial readiness — pure derivation (SEP 5.b.1 §M-Q).
 *
 * Given a quote total and the current balance/allowance snapshot,
 * derive the frontend-only readiness label. No React, no I/O.
 *
 * Truth table (bigint semantics, `>=` not equality):
 *   balance < required            → insufficient_balance
 *   balance >= required && allowance <  required → approval_required
 *   balance >= required && allowance >= required → ready_for_purchase
 *
 * Insufficient balance NEVER collapses into approval_required, even if
 * allowance is also insufficient — the balance shortfall is the primary
 * blocker and approve wouldn't fix it.
 */

export type FinancialReadiness =
  | { kind: "insufficient_balance"; short_by: bigint }
  | { kind: "approval_required"; required_approval: bigint }
  | { kind: "ready_for_purchase" };

export interface FinancialSnapshot {
  balance: bigint;
  allowance: bigint;
  required: bigint;
}

export function deriveFinancialReadiness(
  s: FinancialSnapshot,
): FinancialReadiness {
  if (s.required < 0n) {
    throw new Error("deriveFinancialReadiness: required must be >= 0");
  }
  if (s.balance < s.required) {
    return { kind: "insufficient_balance", short_by: s.required - s.balance };
  }
  if (s.allowance < s.required) {
    return { kind: "approval_required", required_approval: s.required };
  }
  return { kind: "ready_for_purchase" };
}
